import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Coins, Gamepad2, Maximize2, Pencil, Trash2, Zap, Lock, Share2, Shuffle, Download } from "lucide-react";
import { toast, Toaster } from "sonner";
import { toPng } from "html-to-image";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Bracket from "../components/Bracket";
import Countdown from "../components/Countdown";
import MatchEditor from "../components/MatchEditor";
import GroupMatchEditor from "../components/GroupMatchEditor";
import GroupPodium from "../components/GroupPodium";
import EditTournamentModal from "../components/EditTournamentModal";
import EditTeamModal from "../components/EditTeamModal";
import ShareModal from "../components/ShareModal";
import { api, resolveAsset, formatError } from "../lib/api";
import { useLiveWS } from "../hooks/useLiveWS";
import { useAuth } from "../lib/auth";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

export default function BracketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [error, setError] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const bracketRef = useRef(null);

  const handleDownload = async () => {
    if (!bracketRef.current) return;
    setDownloading(true);
    try {
      const node = bracketRef.current;
      // Find the inner scroll container that holds the true bracket geometry
      const scroll = node.querySelector(".bracket-scroll") || node;
      const prevOverflow = scroll.style.overflow;
      const prevMaxH = scroll.style.maxHeight;
      const prevWidth = scroll.style.width;
      scroll.style.overflow = "visible";
      scroll.style.maxHeight = "none";

      // Full dimensions of the bracket (not just the visible viewport)
      const fullW = Math.max(scroll.scrollWidth, scroll.clientWidth);
      const fullH = Math.max(scroll.scrollHeight, scroll.clientHeight);

      const dataUrl = await toPng(scroll, {
        pixelRatio: 2,
        width: fullW,
        height: fullH,
        canvasWidth: fullW,
        canvasHeight: fullH,
        backgroundColor: "#050505",
        cacheBust: true,
        skipFonts: false,
        style: {
          width: `${fullW}px`,
          height: `${fullH}px`,
          maxHeight: "none",
          overflow: "visible",
          transform: "none",
        },
      });

      // Restore original styles
      scroll.style.overflow = prevOverflow || "";
      scroll.style.maxHeight = prevMaxH || "";
      scroll.style.width = prevWidth || "";

      // Composite a subtle "Spicy Community" watermark + red header bar onto the exported PNG
      const finalUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Header bar dimensions — scale with output width
          const bannerH = Math.max(68, Math.round(img.width * 0.045));
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height + bannerH;
          const ctx = canvas.getContext("2d");

          // Red gradient header bar
          const grad = ctx.createLinearGradient(0, 0, canvas.width, 0);
          grad.addColorStop(0, "#8b0000");
          grad.addColorStop(0.5, "#e60000");
          grad.addColorStop(1, "#8b0000");
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, canvas.width, bannerH);

          // Thin darker bottom border for the bar
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.fillRect(0, bannerH - 2, canvas.width, 2);

          // Header text: tournament name on the left, date on the right
          const leftPad = Math.max(28, Math.round(img.width * 0.02));
          const headerFont = Math.max(22, Math.round(bannerH * 0.42));
          ctx.save();
          ctx.font = `900 ${headerFont}px "Inter", "Helvetica Neue", Arial, sans-serif`;
          ctx.fillStyle = "#ffffff";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "rgba(0,0,0,0.4)";
          ctx.shadowBlur = 4;
          const tName = (tournament?.name || "Tournament").toUpperCase();
          // Truncate if absurdly long
          const maxTextW = canvas.width * 0.65;
          let drawn = tName;
          while (ctx.measureText(drawn).width > maxTextW && drawn.length > 4) {
            drawn = drawn.slice(0, -2);
          }
          if (drawn !== tName) drawn += "…";
          ctx.fillText(drawn, leftPad, bannerH / 2);

          // Date on the right
          ctx.textAlign = "right";
          ctx.font = `700 ${Math.round(headerFont * 0.68)}px "Inter", "Helvetica Neue", Arial, sans-serif`;
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          const dateStr = new Date().toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
          });
          ctx.fillText(dateStr, canvas.width - leftPad, bannerH / 2);
          ctx.restore();

          // Draw the bracket below the header bar
          ctx.drawImage(img, 0, bannerH);

          // Bottom-right watermark (subtle), scales with image
          const padding = Math.max(28, Math.round(img.width * 0.012));
          const fontSize = Math.max(18, Math.round(img.width * 0.012));
          ctx.save();
          ctx.textAlign = "right";
          ctx.textBaseline = "bottom";
          ctx.font = `700 ${fontSize}px "Inter", "Helvetica Neue", Arial, sans-serif`;
          const text = "SPICY COMMUNITY";
          const textWidth = ctx.measureText(text).width;
          const dotSize = Math.round(fontSize * 0.35);
          const baseX = canvas.width - padding;
          const baseY = canvas.height - padding;
          ctx.shadowColor = "rgba(0,0,0,0.7)";
          ctx.shadowBlur = Math.round(fontSize * 0.6);
          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.fillText(text, baseX, baseY);
          ctx.shadowBlur = 0;
          const dotX = baseX - textWidth - dotSize * 1.4;
          const dotY = baseY - fontSize * 0.38;
          ctx.fillStyle = "rgba(230,0,0,0.85)";
          ctx.beginPath();
          ctx.arc(dotX, dotY, dotSize / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = dataUrl;
      });

      const link = document.createElement("a");
      link.download = "spicy-community-bracket.png";
      link.href = finalUrl;
      link.click();
      toast.success("Saved spicy-community-bracket.png");
    } catch (e) {
      toast.error(`Couldn't export bracket: ${e.message || "unknown error"}`);
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/tournaments/${id}`);
      setTournament(data.tournament);
      setTeams(data.teams);
      setMatches(data.matches);
    } catch (e) {
      setError(e.response?.status === 404 ? "Tournament not found" : "Failed to load tournament");
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Transient "just updated" highlight map: matchId → true (cleared 3s after last WS push for that match)
  const [recentlyUpdated, setRecentlyUpdated] = useState({});
  const clearTimersRef = useRef({});
  const markJustUpdated = (matchId) => {
    setRecentlyUpdated((prev) => ({ ...prev, [matchId]: Date.now() }));
    if (clearTimersRef.current[matchId]) {
      clearTimeout(clearTimersRef.current[matchId]);
    }
    clearTimersRef.current[matchId] = setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = { ...prev };
        delete next[matchId];
        return next;
      });
      delete clearTimersRef.current[matchId];
    }, 3100);
  };

  useEffect(() => {
    // Clean up any pending timers on unmount
    return () => {
      Object.values(clearTimersRef.current).forEach((t) => clearTimeout(t));
      clearTimersRef.current = {};
    };
  }, []);

  useLiveWS((msg) => {
    if (!tournament) return;
    if (msg.event === "match_updated" && msg.data.tournament_id === tournament.id) {
      setMatches((prev) => prev.map((m) => (m.id === msg.data.id ? msg.data : m)));
      markJustUpdated(msg.data.id);
    } else if (msg.event === "team_updated" && msg.data.tournament_id === tournament.id) {
      setTeams((prev) => prev.map((t) => (t.id === msg.data.id ? msg.data : t)));
    } else if (msg.event === "team_added" && msg.data.tournament_id === tournament.id) {
      setTeams((prev) => [...prev, msg.data]);
    } else if (msg.event === "tournament_updated" && msg.data.id === tournament.id) {
      setTournament(msg.data);
    } else if (msg.event === "bracket_generated" && msg.data.tournament_id === tournament.id) {
      load();
    } else if (msg.event === "teams_shuffled" && msg.data.tournament_id === tournament.id) {
      // A shuffle from another tab/device re-seeds teams and clears any
      // pre-generated bracket, so the safest sync is a full reload.
      load();
    }
  });

  const teamsById = useMemo(() => {
    const m = {};
    teams.forEach((t) => (m[t.id] = t));
    return m;
  }, [teams]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-3">Error</div>
          <h1 className="font-display font-black text-4xl mb-4">{error}</h1>
          <Link to="/tournaments" data-testid="bracket-back-to-list" className="btn-liquid inline-flex">
            <ArrowLeft size={14} /> Back to tournaments
          </Link>
        </div>
        <Footer discordUrl={DISCORD_URL} />
      </div>
    );
  }

  if (!tournament) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading bracket...</div>;
  }

  const cap = tournament.max_teams || 32;
  const slotsUsed = teams.length;
  const slotsLeft = Math.max(0, cap - slotsUsed);
  const canManage = Boolean(
    user && user.id && (user.role === "admin" || (tournament.owner_id && tournament.owner_id === user.id))
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <Navbar />

      {/* Banner hero */}
      <section className="relative overflow-hidden">
        {tournament.banner_url ? (
          <div
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage: `url(${resolveAsset(tournament.banner_url)})`,
              backgroundSize: "cover", backgroundPosition: "center",
              filter: "saturate(1.1)",
            }}
          />
        ) : null}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/70 via-black/85 to-[#050505]" />

        <div className="max-w-7xl mx-auto px-6 pt-10 pb-12">
          <button
            onClick={() => navigate("/tournaments")}
            data-testid="bracket-back"
            className="btn-ghost-glass !py-2 !px-4 !text-xs mb-6"
          >
            <ArrowLeft size={12} /> All tournaments
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <StatusBadge status={tournament.status} />
              {tournament.game && (
                <span className="inline-flex items-center gap-1.5 glass-dark px-3 py-1 rounded-full text-xs font-mono">
                  <Gamepad2 size={10} /> {tournament.game}
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter drop-shadow-lg">
              {tournament.name}
            </h1>
            {tournament.description && (
              <p className="text-zinc-300 mt-3 max-w-3xl">{tournament.description}</p>
            )}

            {/* Countdown (scheduled state) */}
            {tournament.status === "scheduled" && tournament.starts_at && (
              <div className="mt-6 max-w-xl">
                <Countdown startsAt={tournament.starts_at} />
              </div>
            )}

            {/* Go Live button for admin/owner */}
            {canManage && (tournament.status === "scheduled" || tournament.status === "draft") && (
              <div className="mt-6">
                <button
                  onClick={async () => {
                    if (!window.confirm(`Go LIVE with ${tournament.name}? Team roster will be locked and the bracket will be generated if not already.`)) return;
                    try {
                      await api.post(`/tournaments/${tournament.id}/go-live`);
                      toast.success("🔴 Tournament is now LIVE");
                      await load();
                    } catch (e) {
                      toast.error(formatError(e.response?.data?.detail) || e.message);
                    }
                  }}
                  data-testid="go-live-btn"
                  className="btn-liquid btn-liquid-lg pulse-glow"
                >
                  <Zap size={16} /> GO LIVE
                </button>
                <div className="text-xs text-zinc-500 mt-2 font-mono">
                  {teams.length < 2
                    ? "Add at least 2 teams before going live"
                    : `${teams.length} team${teams.length !== 1 ? "s" : ""} ready`}
                </div>
              </div>
            )}

            {/* Info strip */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoTile icon={Coins} label="Prize Pool" value={`$${(tournament.prize_pool || 0).toLocaleString()}`} highlight />
              <InfoTile icon={Users} label="Teams" value={`${slotsUsed} / ${cap}`} sub={slotsLeft > 0 ? `${slotsLeft} slots left` : "Full"} />
              <InfoTile label="Format" value={tournament.format.replace("_", " ")} />
              <InfoTile label="Host" value={tournament.host || "—"} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bracket */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display font-bold text-2xl">Live Bracket</h2>
          <div className="flex gap-2 flex-wrap">
            {canManage && (
              <>
                <button
                  onClick={() => setEditing(true)}
                  data-testid="bp-edit-tournament"
                  className="btn-ghost-glass !py-2 !px-4 !text-xs"
                >
                  <Pencil size={12} /> Edit tournament
                </button>
                {!["live", "finished"].includes(tournament.status) && teams.length >= 2 && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Shuffle team seeds? Any pre-generated bracket will be cleared.")) return;
                      try {
                        await api.post(`/tournaments/${tournament.id}/shuffle`);
                        toast.success("Teams shuffled — seeds re-assigned");
                        await load();
                      } catch (e) {
                        toast.error(formatError(e.response?.data?.detail) || e.message);
                      }
                    }}
                    data-testid="bp-shuffle-teams-btn"
                    className="btn-ghost-glass !py-2 !px-4 !text-xs"
                  >
                    <Shuffle size={12} /> Shuffle teams
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete "${tournament.name}"? This removes the tournament, its teams, matches and feed entries.`)) return;
                    try {
                      await api.delete(`/tournaments/${tournament.id}`);
                      toast.success("Tournament deleted");
                      navigate("/tournaments");
                    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
                  }}
                  data-testid="bp-delete-tournament"
                  className="btn-ghost-glass !py-2 !px-4 !text-xs hover:!text-red-400"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </>
            )}
            <button
              onClick={() => setShareOpen(true)}
              data-testid="bp-share-btn"
              className="btn-ghost-glass !py-2 !px-4 !text-xs"
            >
              <Share2 size={12} /> Share
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading || matches.length === 0}
              data-testid="bp-download-btn"
              className="btn-ghost-glass !py-2 !px-4 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              title={matches.length === 0 ? "Generate the bracket first" : "Download as PNG"}
            >
              <Download size={12} /> {downloading ? "Rendering…" : "Download Bracket"}
            </button>
            <button
              onClick={() => setFocusMode((v) => !v)}
              data-testid="bracket-focus-toggle"
              className="btn-ghost-glass !py-2 !px-4 !text-xs"
            >
              <Maximize2 size={12} /> {focusMode ? "Exit focus" : "Focus mode"}
            </button>
          </div>
        </div>

        <div ref={bracketRef} className="glass-dark rounded-2xl overflow-hidden">
          {matches.length > 0 ? (
            <Bracket
              matches={matches}
              teamsById={teamsById}
              format={tournament.format}
              onMatchClick={(m) => setActiveMatch(m)}
              recentlyUpdated={recentlyUpdated}
              championTeamId={tournament.winner_team_id}
            />
          ) : (
            <div className="text-center text-zinc-500 py-24">
              Bracket not yet generated. {slotsLeft > 0 && "Waiting for more teams."}
            </div>
          )}
        </div>

        {tournament.format === "group_advancement" && tournament.status === "finished" && (() => {
          const finalMatch = [...matches]
            .filter((m) => (m.participant_ids || []).length === 4 && !(m.next_match_ids_by_rank || []).some(Boolean))
            .sort((a, b) => b.round - a.round)[0];
          const placements = finalMatch?.final_placements || [];
          return placements.length >= 2 ? (
            <GroupPodium placements={placements} teamsById={teamsById} tournamentId={tournament.id} />
          ) : null;
        })()}

        {/* Standings */}
        {teams.length > 0 && (
          <div className="glass-dark rounded-2xl p-6 mt-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-xl">Team Standings</h3>
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">
                {slotsUsed} / {cap} spots filled
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(() => {
                const preLive = !["live", "finished"].includes(tournament.status);
                const ordered = [...teams].sort((a, b) =>
                  preLive
                    ? (a.seed || 0) - (b.seed || 0)
                    : (b.wins || 0) - (a.wins || 0)
                );
                return ordered.map((t, i) => (
                <div
                  key={t.id}
                  data-testid={`bp-standing-${t.id}`}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04]"
                >
                  <span className="font-mono text-xs text-zinc-500 w-6 text-right">{i + 1}.</span>
                  {t.logo_url ? (
                    <img src={resolveAsset(t.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="h-8 w-8 rounded-full flex items-center justify-center font-display font-bold text-xs" style={{ background: t.color || "#E60000" }}>
                      {(t.name || "").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{t.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      <span className="text-emerald-400">{t.wins || 0}W</span> <span className="text-red-400">{t.losses || 0}L</span>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditingTeam(t)}
                        data-testid={`bp-edit-team-${t.id}`}
                        className="h-6 w-6 rounded-full glass-red flex items-center justify-center hover:text-white"
                        title="Edit"
                      >
                        <Pencil size={10} />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${t.name}?`)) return;
                          try {
                            await api.delete(`/teams/${t.id}`);
                            toast.success("Team removed");
                            await load();
                          } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
                        }}
                        data-testid={`bp-delete-team-${t.id}`}
                        className="h-6 w-6 rounded-full glass-red flex items-center justify-center hover:text-red-400"
                        title="Remove"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  )}
                </div>
              ));
              })()}
            </div>
          </div>
        )}
      </section>

      {activeMatch && (
        <div
          onClick={() => setActiveMatch(null)}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="bp-match-modal"
            className="glass-red-glow rounded-2xl p-8 max-w-md w-full"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-red-300 font-mono mb-2">
              {activeMatch.round_name || "Match"}
            </div>
            <h3 className="font-display font-bold text-2xl mb-6">
              {canManage && ["live", "finished"].includes(tournament.status) ? "Manage Match" : "Match Details"}
            </h3>
            {canManage && ["live", "finished"].includes(tournament.status) ? (
              activeMatch.participant_ids && activeMatch.participant_ids.length === 4 ? (
                <GroupMatchEditor
                  match={activeMatch}
                  teamsById={teamsById}
                  onSaved={async (updated) => {
                    setActiveMatch(updated);
                    await load();
                  }}
                />
              ) : (
                <MatchEditor
                  match={activeMatch}
                  teamA={teamsById[activeMatch.team_a_id]}
                  teamB={teamsById[activeMatch.team_b_id]}
                  onSaved={async (updated) => {
                    setActiveMatch(updated);
                    await load();
                  }}
                />
              )
            ) : (
              activeMatch.participant_ids && activeMatch.participant_ids.length === 4 ? (
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((slot) => {
                    const pid = activeMatch.participant_ids[slot];
                    const tm = pid ? teamsById[pid] : null;
                    const sc = (activeMatch.participant_scores || [])[slot] ?? 0;
                    const placements = activeMatch.final_placements || [];
                    const rank = pid ? (placements.indexOf(pid) + 1) : 0;
                    const advancing = rank === 1 || rank === 2;
                    return (
                      <div
                        key={slot}
                        className={`flex items-center justify-between rounded-xl p-3 border
                          ${advancing ? "border-yellow-400/40 bg-yellow-400/5" : "border-white/10 bg-black/30"}`}
                      >
                        <span className={`font-semibold ${advancing ? "text-white" : "text-zinc-400"}`}>
                          {tm?.name || "TBD"}
                        </span>
                        <div className="flex items-center gap-2">
                          {rank ? (
                            <span className={`text-[10px] font-mono tracking-widest px-1.5 py-0.5 rounded
                              ${advancing ? "text-yellow-200 bg-yellow-400/20" : "text-zinc-500 bg-white/5"}`}>
                              #{rank}
                            </span>
                          ) : null}
                          <span className="font-mono text-xl font-bold text-red-300">{sc}</span>
                        </div>
                      </div>
                    );
                  })}
                  {canManage && tournament.status !== "live" && (
                    <div className="text-xs text-zinc-500 pt-3 flex items-center gap-2">
                      <Lock size={12} /> Match editing is enabled once the tournament is LIVE.
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {["a", "b"].map((slot) => {
                    const tid2 = slot === "a" ? activeMatch.team_a_id : activeMatch.team_b_id;
                    const sc = slot === "a" ? activeMatch.score_a : activeMatch.score_b;
                    const tm = teamsById[tid2];
                    const isWin = activeMatch.winner_id && activeMatch.winner_id === tid2;
                    return (
                      <div key={slot} className={`flex items-center justify-between rounded-xl p-4 ${isWin ? "glass-red" : "glass-dark"}`}>
                        <span className={`font-semibold ${isWin ? "text-white" : "text-zinc-400"}`}>{tm?.name || "TBD"}</span>
                        <span className="font-mono text-2xl font-bold text-red-300">{sc}</span>
                      </div>
                    );
                  })}
                  {canManage && tournament.status !== "live" && (
                    <div className="text-xs text-zinc-500 pt-3 flex items-center gap-2">
                      <Lock size={12} /> Match editing is enabled once the tournament is LIVE.
                    </div>
                  )}
                </div>
              )
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setActiveMatch(null)}
                data-testid="bp-close-modal"
                className="btn-ghost-glass !py-2 !px-5"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer discordUrl={DISCORD_URL} />

      {editing && tournament && (
        <EditTournamentModal
          tournament={tournament}
          teams={teams}
          onClose={() => setEditing(false)}
          onSaved={() => load()}
          onDeleted={() => navigate("/tournaments")}
        />
      )}
      {editingTeam && (
        <EditTeamModal
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSaved={() => load()}
          onDeleted={() => load()}
        />
      )}
      {shareOpen && tournament && (
        <ShareModal
          tournament={tournament}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function InfoTile({ icon: Icon, label, value, sub, highlight }) {
  return (
    <div className={`rounded-xl p-4 ${highlight ? "glass-red-glow" : "glass-dark"}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={12} className="text-red-400" />}
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      </div>
      <div className="font-display font-bold text-xl truncate">{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { cls: "bg-zinc-800/60 text-zinc-300 border-white/10", label: "Draft" },
    scheduled: { cls: "bg-amber-900/40 text-amber-300 border-amber-500/30", label: "Scheduled" },
    upcoming: { cls: "bg-amber-900/40 text-amber-300 border-amber-500/30", label: "Scheduled" },
    live: { cls: "bg-red-900/60 text-red-200 border-red-500/40", label: "Live Now", live: true },
    finished: { cls: "bg-emerald-900/40 text-emerald-300 border-emerald-500/30", label: "Finished" },
    completed: { cls: "bg-emerald-900/40 text-emerald-300 border-emerald-500/30", label: "Finished" },
  };
  const cfg = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs uppercase tracking-widest font-mono ${cfg.cls}`}>
      {cfg.live && <span className="live-dot" />}
      {cfg.label}
    </span>
  );
}
